// src/PrivacyPolicy.tsx

import React from 'react';

const PrivacyPolicy: React.FC = () => (
  <div className="text-white">
    {/* Document Header */}
    <header className="mb-12 text-center">
      <h1 className="text-3xl font-bold tracking-tight mb-4">
        Privacy Policy
      </h1>
      <p className="text-white/60">
        Last updated: January 18, 2025
      </p>
    </header>

    <div className="space-y-10">
      {/* Introduction */}
      <section className="leading-relaxed">
        <p>
          This Privacy Policy governs how topoSphere.net ("operator," "we," "our," or "us") 
          collects, uses, maintains, and discloses information from users ("User" or "you") 
          of our application forms. This policy applies worldwide, regardless of your access location.
        </p>
      </section>

      {/* Main Content Sections */}
      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          1. Introduction
        </h2>
        <div className="space-y-4 pl-4">
          <p>
            We are committed to safeguarding your personal information. This Privacy Policy 
            explains our practices for collecting, processing, storing, and protecting information 
            you provide when using our online forms and related services. By submitting your 
            personal information, you agree to these terms.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          2. Information We Collect
        </h2>
        <div className="space-y-6 pl-4">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Personal Information</h3>
            <p className="pl-4">
              When you submit our application form, we collect the following required information:
              name, work email address, job title, and organization name. You may also choose to 
              provide additional details about your visualization use case and requirements.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Non-Personal Information</h3>
            <p className="pl-4">
              We automatically collect certain technical data such as IP address, browser type, 
              and operating system information. This helps us optimize your experience and improve 
              our services. We may also use cookies and similar tracking technologies to enhance 
              site functionality.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          3. How We Use Your Information
        </h2>
        <div className="space-y-4 pl-4">
          <p>We use your information for the following purposes:</p>
          <ul className="space-y-2 pl-4">
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Processing your Technical Preview Program application</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Providing access to topoSphere SDK and related materials</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Communicating updates about products and services</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Sending technical documentation and support information</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Marketing communications (with your consent)</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          4. Data Protection and Security
        </h2>
        <div className="space-y-4 pl-4">
          <p>
            We implement appropriate technical and organizational measures to protect your 
            information. These include:
          </p>
          <ul className="space-y-2 pl-4">
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Encryption of data in transit using TLS</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Secure data storage with encryption at rest</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Access controls and authentication mechanisms</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Regular security assessments and updates</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          5. Your Rights
        </h2>
        <div className="space-y-4 pl-4">
          <p>You have the following rights regarding your personal information:</p>
          <ul className="space-y-2 pl-4">
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Access your personal information</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Request corrections to inaccurate data</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Request deletion of your information</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Withdraw consent for marketing communications</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">→</span>
              <span>Object to certain processing activities</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-bold border-b border-white/20 pb-2">
          6. Contact Information
        </h2>
        <div className="space-y-4 pl-4">
          <p>
            For questions or concerns about this Privacy Policy or your personal information, 
            please contact us:
          </p>
          <div className="pl-4 font-mono text-sm space-y-1">
            <p>Support: support@toposphere.net</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-12 pt-8 border-t border-white/20">
        <p className="text-center text-white/60 text-sm">
          By using our services, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </footer>
    </div>
  </div>
);

export default PrivacyPolicy;